import { forwardRef, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  useHistoryStore,
  selectHistoryEntries,
  selectHistoryFilters,
  type HistoryEntry
} from '../state/historyStore';
import { HistoryFilters } from './HistoryFilters';
import { ComparisonViewer } from './ComparisonViewer';

const inspirationCardVariants = [
  {
    key: 'personal',
    gradient: 'from-[#F6D2FF]/35 via-[#FFD4E9]/10 to-transparent',
    placeholderGradient: 'from-[#FBE8FF]/35 via-transparent to-transparent',
    borderAccent: 'hover:border-fuchsia-200/70',
    glow: 'hover:shadow-[0_28px_60px_rgba(246,210,255,0.35)]'
  },
  {
    key: 'cosplay',
    gradient: 'from-[#FFE1C7]/40 via-[#FCDFA5]/20 to-transparent',
    placeholderGradient: 'from-[#FFEED1]/40 via-transparent to-transparent',
    borderAccent: 'hover:border-amber-200/70',
    glow: 'hover:shadow-[0_28px_60px_rgba(255,214,161,0.35)]'
  },
  {
    key: 'avatar',
    gradient: 'from-[#C5F1FF]/35 via-[#A5EBFF]/20 to-transparent',
    placeholderGradient: 'from-[#D0F6FF]/40 via-transparent to-transparent',
    borderAccent: 'hover:border-cyan-200/70',
    glow: 'hover:shadow-[0_28px_60px_rgba(153,224,255,0.35)]'
  }
] as const;

const inspirationIllustrations: Record<(typeof inspirationCardVariants)[number]['key'], string> = {
  personal: '/images/sections/left.webp',
  cosplay: '/images/sections/middle.webp',
  avatar: '/images/sections/right.webp'
};

interface HistoryPanelProps {
  onSelectOriginal?: (entry: HistoryEntry) => void | Promise<void>;
}

export const HistoryPanel = forwardRef<HTMLDivElement, HistoryPanelProps>(
  function HistoryPanel({ onSelectOriginal }, ref) {
    const { t, i18n } = useTranslation();
    const entries = useHistoryStore(selectHistoryEntries);
    const filters = useHistoryStore(selectHistoryFilters);
    const clear = useHistoryStore((state) => state.clear);
    const removeEntry = useHistoryStore((state) => state.removeEntry);
    const storageNotice = useHistoryStore((state) => state.storageNotice);
    const acknowledgeNotice = useHistoryStore((state) => state.acknowledgeStorageNotice);
    const initialize = useHistoryStore((state) => state.initialize);
    const isInitialized = useHistoryStore((state) => state.isInitialized);
    const storageBytes = useHistoryStore((state) => state.storageBytes);
    const storageLimitBytes = useHistoryStore((state) => state.storageLimitBytes);
    const [inspirationPreviewKey, setInspirationPreviewKey] = useState<
      (typeof inspirationCardVariants)[number]['key'] | null
    >(null);

    const closeInspirationPreview = () => {
      setInspirationPreviewKey(null);
    };

    useEffect(() => {
      void initialize();
    }, [initialize]);
    const handleClear = () => {
      if (typeof window === 'undefined') {
        return;
      }
      const confirmed = window.confirm(t('history.confirmations.clear'));
      if (confirmed) {
        clear();
      }
    };
    const handleRemove = (id: string) => {
      if (typeof window === 'undefined') {
        return;
      }
      const confirmed = window.confirm(t('history.confirmations.delete'));
      if (confirmed) {
        removeEntry(id);
      }
    };

    const filteredEntries = useMemo(() => {
      return entries
        .filter((entry) => {
          const styleMatch = filters.styleId === 'all' || entry.style.id === filters.styleId;
          const dateMatch = filters.date === 'all' || entry.createdAt.slice(0, 10) === filters.date;
          return styleMatch && dateMatch;
        })
        .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    }, [entries, filters]);

    const groupedEntries = useMemo(() => {
      const groups = new Map<string, { entries: HistoryEntry[]; timestamp: number }>();

      filteredEntries.forEach((entry) => {
        const groupKey = entry.createdAt.slice(0, 10);
        const existing = groups.get(groupKey);
        if (existing) {
          existing.entries.push(entry);
        } else {
          groups.set(groupKey, {
            entries: [entry],
            timestamp: new Date(entry.createdAt).getTime()
          });
        }
      });

      return Array.from(groups.entries()).map(([key, value]) => ({
        key,
        entries: value.entries,
        count: value.entries.length,
        timestamp: value.timestamp
      }));
    }, [filteredEntries]);

    const dateFormatter = useMemo(() => {
      try {
        return new Intl.DateTimeFormat(i18n.language, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch {
        return new Intl.DateTimeFormat('en', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      }
    }, [i18n.language]);

    const dateTimeFormatter = useMemo(() => {
      try {
        return new Intl.DateTimeFormat(i18n.language, {
          dateStyle: 'medium',
          timeStyle: 'short'
        });
      } catch {
        return new Intl.DateTimeFormat('en', {
          dateStyle: 'medium',
          timeStyle: 'short'
        });
      }
    }, [i18n.language]);

    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

    useEffect(() => {
      if (!groupedEntries.length) {
        setExpandedGroups([]);
        return;
      }
      const groupKeys = groupedEntries.map((group) => group.key);
      setExpandedGroups((prev) => {
        const filtered = prev.filter((key) => groupKeys.includes(key));
        if (filtered.length) {
          const unchanged =
            filtered.length === prev.length &&
            filtered.every((key, index) => key === prev[index]);
          return unchanged ? prev : filtered;
        }
        return [groupedEntries[0].key];
      });
    }, [groupedEntries]);

    useEffect(() => {
      if (!inspirationPreviewKey || typeof window === 'undefined') {
        return;
      }
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeInspirationPreview();
        }
      };
      const body = document.body;
      const previousOverflow = body.style.overflow;
      body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        body.style.overflow = previousOverflow;
      };
    }, [inspirationPreviewKey]);

    const toggleGroup = (key: string) => {
      setExpandedGroups((prev) => {
        if (prev.includes(key)) {
          return prev.filter((item) => item !== key);
        }
        return [...prev, key];
      });
    };

    const formatBytes = useMemo(() => {
      const formatter = new Intl.NumberFormat(i18n.language, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 0
      });
      return (bytes: number) => formatter.format(bytes / (1024 * 1024));
    }, [i18n.language]);

    const storageMessage = useMemo(() => {
      if (!storageNotice) {
        return null;
      }
      if (storageNotice === 'quota' && storageLimitBytes) {
        return t(`history.storage.${storageNotice}.message`, {
          used: formatBytes(storageBytes),
          limit: formatBytes(storageLimitBytes)
        });
      }
      return t(`history.storage.${storageNotice}.message`);
    }, [formatBytes, storageBytes, storageLimitBytes, storageNotice, t]);

    const storageBanner = storageNotice ? (
      <div
        role="alert"
        className="flex flex-col gap-3 rounded-3xl border border-ai-warning/40 bg-ai-warning/10 p-4 text-sm text-ai-text-primary shadow-ai-card"
      >
        <div className="space-y-1">
          <p className="font-semibold text-ai-warning/90">
            {t(`history.storage.${storageNotice}.title`)}
          </p>
          <p className="text-xs text-ai-text-secondary">
            {storageMessage}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {storageNotice === 'quota' ? (
            <>
              <button
                type="button"
                className="rounded-full border border-ai-warning/50 px-4 py-2 text-xs font-semibold text-ai-warning transition-colors hover:bg-ai-warning/20 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                onClick={handleClear}
              >
                {t('history.storage.quota.actionClear')}
              </button>
              <button
                type="button"
                className="rounded-full border border-ai-surface-glow/40 px-4 py-2 text-xs font-semibold text-ai-text-secondary transition-colors hover:border-ai-primary/40 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                onClick={acknowledgeNotice}
              >
                {t('history.storage.quota.actionDismiss')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="rounded-full border border-ai-surface-glow/40 px-4 py-2 text-xs font-semibold text-ai-text-secondary transition-colors hover:border-ai-primary/40 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
              onClick={acknowledgeNotice}
            >
              {t('history.storage.error.actionDismiss')}
            </button>
          )}
        </div>
      </div>
    ) : null;

    const inspirationSection = (
      <div className="space-y-3">
        <div className="rounded-3xl border border-ai-surface-glow/40 bg-ai-surface-elevated/80 p-3 shadow-ai-card">
          <div className="grid gap-4 md:grid-cols-3">
            {inspirationCardVariants.map((card) => (
              <article
                key={card.key}
                tabIndex={0}
                className={`group relative flex min-h-[280px] flex-col justify-between overflow-hidden rounded-3xl border border-ai-surface-glow/35 bg-ai-surface-elevated/80 px-5 py-6 transition-all duration-300 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus ${card.borderAccent} ${card.glow}`}
              >
                <div
                  aria-hidden="true"
                  className={`pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br opacity-40 transition-opacity duration-300 group-hover:opacity-100 ${card.gradient}`}
                />
                <div className="relative z-10 space-y-3">
                  <p className="font-display text-xl font-semibold text-ai-text-primary">
                    {t(`history.inspiration.cards.${card.key}.title`)}
                  </p>
                  <p className="text-sm leading-relaxed text-ai-text-secondary">
                    {t(`history.inspiration.cards.${card.key}.description`)}
                  </p>
                </div>
                <div className="relative z-10 mt-6">
                  <button
                    type="button"
                    className={`group/button relative flex h-32 w-full items-center justify-center overflow-hidden rounded-2xl border border-ai-surface-glow/40 bg-gradient-to-br ${card.placeholderGradient} text-center transition focus:outline-none focus-visible:ring focus-visible:ring-ai-focus`}
                    onClick={() => setInspirationPreviewKey(card.key)}
                  >
                    <img
                      src={inspirationIllustrations[card.key]}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-tr from-white/15 via-transparent to-white/5 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    );

    const inspirationPreviewImage = inspirationPreviewKey
      ? inspirationIllustrations[inspirationPreviewKey]
      : null;
    const inspirationPreviewPortal =
      inspirationPreviewImage && typeof document !== 'undefined'
        ? createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3"
            role="dialog"
            aria-modal="true"
            onClick={closeInspirationPreview}
          >
            <div
              className="relative w-full max-w-4xl overflow-hidden rounded-3xl border border-ai-surface-glow/40 bg-ai-surface shadow-ai-card"
              onClick={(event) => event.stopPropagation()}
              tabIndex={-1}
            >
              <button
                type="button"
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full border border-ai-surface-glow/40 bg-ai-surface-elevated/80 text-ai-text-primary shadow-ai-card transition hover:bg-ai-primary/80 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                onClick={closeInspirationPreview}
                aria-label={t('history.preview.close')}
              >
                <span aria-hidden="true" className="text-xl font-semibold">
                  ×
                </span>
              </button>
              <img
                src={inspirationPreviewImage}
                className="max-h-[80vh] w-full object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>,
          document.body
        )
        : null;

    if (!isInitialized) {
      return (
        <>
          <section className="space-y-2">
            {storageBanner}
            {inspirationSection}
            <div className="rounded-3xl border border-ai-surface-glow/30 bg-ai-surface-elevated/70 p-3 shadow-ai-card">
              <p className="text-sm text-ai-text-secondary">{t('history.loading')}</p>
            </div>
          </section>
          {inspirationPreviewPortal}
        </>
      );
    }

    if (!groupedEntries.length) {
      return (
        <>
          <section className="space-y-2">
            {storageBanner}
            {inspirationSection}
            <div className="relative flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-semibold text-ai-text-primary">
                {t('history.heading')}
              </h2>
              <span className="w-full text-center text-[11px] uppercase tracking-[0.2em] text-ai-text-secondary md:absolute md:left-1/2 md:w-auto md:-translate-x-1/2">
                {t('history.headingHint')}
              </span>
            </div>
            <p className="rounded-2xl border border-ai-surface-glow/20 bg-ai-surface-elevated/60 p-3 text-sm text-ai-text-secondary">
              {t('history.empty')}
            </p>
          </section>
          {inspirationPreviewPortal}
        </>
      );
    }

    return (
      <>
        <section className="space-y-2">
          {storageBanner}
          {inspirationSection}
          <div ref={ref} className="relative flex flex-wrap items-center justify-between gap-2">
            <span className="font-display text-xl font-semibold text-ai-text-primary">
              {t('history.heading')}
            </span>
            <span className="w-full text-center text-[11px] uppercase tracking-[0.2em] text-ai-text-secondary md:absolute md:left-1/2 md:w-auto md:-translate-x-1/2">
              {t('history.headingHint')}
            </span>
            <button
              type="button"
              className="rounded-full border border-ai-surface-glow/30 px-4 py-2 text-xs font-semibold text-ai-text-secondary transition-colors hover:border-ai-primary/40 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
              onClick={handleClear}
            >
              {t('history.actions.clear')}
            </button>
          </div>

          <HistoryFilters />

          <div className="space-y-3">
            {groupedEntries.map((group) => {
              const isExpanded = expandedGroups.includes(group.key);
              const safeKey = group.key.replace(/[^a-zA-Z0-9]/g, '-');
              const panelId = `history-group-${safeKey}`;
              const rawDate = new Date(group.timestamp);
              const formattedDate = Number.isNaN(rawDate.getTime())
                ? group.key
                : dateFormatter.format(rawDate);
              const groupTitle =
                group.count === 1
                  ? t('history.group.labelSingular', { date: formattedDate })
                  : t('history.group.labelPlural', { date: formattedDate, count: group.count });

              return (
                <article
                  key={group.key}
                  className="rounded-3xl border border-ai-surface-glow/30 bg-ai-surface-elevated/70 shadow-ai-card"
                >
                  <header className="flex flex-wrap items-center justify-between gap-2 px-5 py-2">
                    <div>
                      <h3 className="font-display text-lg font-semibold text-ai-text-primary">
                        {groupTitle}
                      </h3>
                    </div>
                    <button
                      type="button"
                      className="flex items-center gap-2 rounded-full border border-ai-surface-glow/30 px-4 py-2 text-xs font-semibold text-ai-text-secondary transition-colors hover:border-ai-primary/40 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                      aria-expanded={isExpanded}
                      aria-controls={panelId}
                      onClick={() => toggleGroup(group.key)}
                    >
                      <span>
                        {isExpanded
                          ? t('history.group.toggle.collapse', { date: formattedDate })
                          : t('history.group.toggle.expand', { date: formattedDate })}
                      </span>
                      <svg
                        aria-hidden="true"
                        className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 8l5 5 5-5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </header>
                  {isExpanded && (
                    <div id={panelId} className="grid gap-4 px-5 pb-2 md:grid-cols-2">
                      {group.entries.map((entry) => {
                        const entryDate = new Date(entry.createdAt);
                        const formattedEntryDate = Number.isNaN(entryDate.getTime())
                          ? entry.createdAt
                          : dateTimeFormatter.format(entryDate);

                        return (
                          <article
                            key={entry.id}
                            className="flex flex-col gap-3 rounded-3xl border border-ai-surface-glow/30 bg-ai-surface-elevated/60 px-4 py-3"
                          >
                            <header className="flex flex-wrap items-center justify-between gap-2">
                              <div className="flex flex-wrap items-baseline gap-1.5">
                                <h4 className="text-base font-semibold text-ai-text-primary">
                                  {entry.style.name}
                                </h4>
                                <span className="text-[11px] uppercase tracking-[0.3em] text-ai-text-secondary">
                                  {formattedEntryDate}
                                </span>
                              </div>
                              <button
                                type="button"
                                className="rounded-full border border-ai-surface-glow/30 px-3 py-1 text-[11px] font-semibold text-ai-text-secondary transition-colors hover:border-ai-primary/40 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                                onClick={() => handleRemove(entry.id)}
                              >
                                {t('history.actions.delete')}
                              </button>
                            </header>
                            <ComparisonViewer entry={entry} onSelectOriginal={onSelectOriginal} />
                          </article>
                        );
                      })}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
        {inspirationPreviewPortal}
      </>
    );
  }
);
